'use client'

import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'

import { PLATFORM_BLOCK_ANCHOR_ID } from '@/lib/navigation/scrollToAnchor'
import { RouteIntlMessagesProvider } from '@/shared/components/RouteIntlMessagesProvider'

const HomeBelowFold = dynamic(() => import('./index'), {
  ssr: false
})

type HomeBelowFoldLazyProps = {
  locale: string
  messages: Record<string, unknown>
}

function shouldLoadBelowFoldImmediately(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return window.location.hash === `#${PLATFORM_BLOCK_ANCHOR_ID}`
}

export default function HomeBelowFoldLazy({
  locale,
  messages
}: HomeBelowFoldLazyProps) {
  // Always start false so the first client render matches the SSR HTML (which
  // renders null). Reading window.location.hash in the useState initialiser ran
  // during hydration and, when the URL already carried #platform-block, produced
  // a mismatch that blanked the below-fold sections (React #418, OCE). The hash
  // is checked in the effect below instead, after mount.
  const [shouldLoad, setShouldLoad] = useState(false)

  useEffect(() => {
    if (shouldLoad) {
      return
    }

    const activate = () => {
      setShouldLoad(true)
    }

    // Direct entry with #platform-block (e.g. "How it works", reload while
    // scrolled): reveal the sections right after mount — post-hydration, so no
    // SSR/CSR mismatch.
    if (shouldLoadBelowFoldImmediately()) {
      activate()
      return
    }

    const onHashChange = () => {
      if (shouldLoadBelowFoldImmediately()) {
        activate()
      }
    }

    const onScroll = () => {
      activate()
    }

    window.addEventListener('hashchange', onHashChange)
    window.addEventListener('scroll', onScroll, {
      once: true,
      passive: true
    })

    return () => {
      window.removeEventListener('hashchange', onHashChange)
      window.removeEventListener('scroll', onScroll)
    }
  }, [shouldLoad])

  if (!shouldLoad) {
    return null
  }

  return (
    <RouteIntlMessagesProvider locale={locale} messages={messages}>
      <HomeBelowFold />
    </RouteIntlMessagesProvider>
  )
}
